import {map, Observable} from 'rxjs';
import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Environment, Hold} from '@raven';
import {Patron, PatronAccountStatus, PatronRegistrationValue} from './patron.types';
import {ResponseBase} from '@store/common/common.types';
import {isPopulatedString} from '@store/common/typing.helpers';
import {Branch} from '@store/branches/branch.types';

@Injectable({providedIn: 'root'})
export class PatronService {

  private readonly http: HttpClient = inject(HttpClient);
  private readonly environment: Environment = inject(Environment);

  loadAuthPatron(): Observable<Patron> {
    const url = `${this.environment.apiUrl}/patrons/v2/patron`;
    return this.http.get<ResponseBase<Patron>>(url)
      .pipe(map(response => response.objects[0]))
  }

  public lookupAccount(cardNumber: string, PIN: string): Observable<{ patron: Patron, loginToken: string }> {
    const url = `${this.environment.apiUrl}/public/v1/patrons/login`;
    return this.http.post<ResponseBase<{ patron: Patron, loginToken: string }>>(url, {barcode: cardNumber, pin: PIN})
      .pipe(
        map(response => response.objects[0]),
        map(({patron, loginToken}) => {
          if (isPopulatedString(patron.googleUid)) {
            throw new Error('Patron already has online access.');
          }
          return ({patron, loginToken: loginToken.slice()});
        })
      );
  }

  public validatePatronEmailAvailable(email: string): Observable<boolean> {
    const url = `${this.environment.apiUrl}/public/v1/patrons/existing-email?email=${encodeURIComponent(email)}`;
    return this.http.get<boolean>(url);
  }

  public requestVerificationEmail(email: string): Observable<string> {
    const url = `${this.environment.apiUrl}/public/v1/patrons/request-verification-email`;
    return this.http.post<ResponseBase<string>>(url, {email})
      .pipe(map(response => response.objects[0]));
  }

  public verifyEmail(code: string, verificationUUID: string): Observable<never> {
    const url = `${this.environment.apiUrl}/public/v1/patrons/verify-email`;
    return this.http.post<never>(url, {code, verificationUUID});
  }

  public loadAccountStatus(): Observable<PatronAccountStatus> {
    const url = `${this.environment.apiUrl}/patrons/v2/patrons/account/status`;

    return this.http.get<ResponseBase<PatronAccountStatus>>(url).pipe(
      map(response => response.objects[0])
    );
  }

  public register(value: PatronRegistrationValue): Observable<never> {
    const url = `${this.environment.apiUrl}/public/v2/patrons/register`;
    const data = {
      ...value,
      mobilePhone: (value.mobilePhone ?? '').replace(/\D/g, '') || null
    };
    return this.http.post<never>(url, data);
  }

  public registerExisting(value: PatronRegistrationValue, verificationUUID: string): Observable<never> {
    const url = `${this.environment.apiUrl}/public/v1/patrons/register-existing`;
    const data = {
      password: value.password,
      branchId: value.branchId,
      verificationUUID
    };
    return this.http.post<never>(url, data);
  }

  public loadPickupLocationOptions(): Observable<Branch[]> {
    const url = `${this.environment.apiUrl}/patrons/v1/branches/pickup-locations`;
    return this.http.get<ResponseBase<Branch>>(url).pipe(
      map(response => response.objects)
    );
  }

  public placeHold(catalogRecordId: number, pickupBranchId: number): Observable<Hold> {
    const url = `${this.environment.apiUrl}/patrons/v5/holds`;
    return this.http.post<ResponseBase<Hold>>(url, {catalogRecordId, pickupBranchId}).pipe(
      map(response => response.objects[0])
    );
  }
}

export function handlePlaceHoldError(error: HttpErrorResponse): string | undefined {
  const code = error?.['error']?.['errors']?.[0]?.code;
  switch (code) {
    case 'CIRCULATION_RULE_VIOLATION':
      return 'Hold could not be placed due to maximum hold limit reached.';
    case 'CIRCULATION_INVALID_ITEM_ID':
      return 'Hold could not be placed, this record has no copies in circulation.';
    case 'PATRON_INVALID_STANDING_FOR_ACTION':
      return 'Hold could not be placed due to restrictions on your account.';
    case 'BRANCH_INVALID_ID':
      return 'SUPPRESS_ERROR';
    default:
      return undefined;
  }
}

export function handleItemRequestError(error: HttpErrorResponse): string | undefined {
  const code = error?.['error']?.['errors']?.[0]?.code;
  switch (code) {
    case 'ACTION_BLOCKED':
      return 'Item requests are unavailable due to a block on your account.';
    case 'MONTHLY_ITEM_REQUEST_LIMIT_EXCEEDED':
      return 'Maximum number of requests reached.';
    default:
      return undefined;
  }
}
